import React, { useState } from 'react';
import { useToast } from './Toast';

interface ResetPanelProps {
  entityNames: string[];
  onReset: (entityName?: string) => Promise<void>;
}

export function ResetPanel({ entityNames, onReset }: ResetPanelProps) {
  const [isResetting, setIsResetting] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState('');
  const { showToast } = useToast();

  async function handleFullReset() {
    if (!confirm('Are you sure you want to reset ALL data to seed state? This cannot be undone.')) return;
    setIsResetting(true);
    try {
      await onReset();
      showToast('Full reset complete. All entities restored to seed data.');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setIsResetting(false);
    }
  }

  async function handleEntityReset() {
    if (!selectedEntity) return;
    if (!confirm(`Reset ${selectedEntity} to seed data?`)) return;
    setIsResetting(true);
    try {
      await onReset(selectedEntity);
      showToast(`Reset ${selectedEntity} to seed data`);
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setIsResetting(false);
    }
  }

  return (
    <div className="max-w-xl">
      <h2 className="text-xl font-semibold text-slate-900 mb-6">Data Reset</h2>

      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <h3 className="text-lg font-medium text-slate-800 mb-2">Full Reset</h3>
        <p className="text-sm text-slate-600 mb-4">
          Restore all entities to their original seed data state.
        </p>
        <button
          onClick={handleFullReset}
          disabled={isResetting}
          className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >
          {isResetting ? 'Resetting...' : 'Reset All Data'}
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-medium text-slate-800 mb-2">Per-Entity Reset</h3>
        <p className="text-sm text-slate-600 mb-4">
          Reset a single entity type to its seed data.
        </p>
        <div className="flex gap-3">
          <select
            value={selectedEntity}
            onChange={e => setSelectedEntity(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
          >
            <option value="">Select entity...</option>
            {entityNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <button
            onClick={handleEntityReset}
            disabled={isResetting || !selectedEntity}
            className="px-4 py-2 text-sm rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
          >
            Reset Entity
          </button>
        </div>
      </div>
    </div>
  );
}
